import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { createRef, useEffect, useRef, useState } from 'react';
import type { ServicesProps, ServicesRef } from './Services.data';
import styles from './Services.module.scss';
import {
  setupTransitionInTimeline,
  setupSlidesTimeline,
  setupImagesTimeline,
  setupLottieTimeline,
} from './Services.transitions';
import { useScrollTransition } from 'util/react-transition-component';
import Service from './service/Service';
import Image from '../../atoms/image/Image';
import LottieView from '../../atoms/lottie-view/LottieView';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import { cleanMediaQueries } from '../../../hooks/useDeviceStateTracker';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import type { LottieViewRef } from '../../atoms/lottie-view/LottieView.data';
import type { ServiceRef } from './service/Service.data';

export default function Services({
  canTransitionIn,
  className,
  blok,
  ref,
}: ServicesProps & { ref?: Ref<ServicesRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const servicesContainerRef = useRef<HTMLDivElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const imageContainers = useRef(blok.services.map(() => createRef<HTMLDivElement>()));
  const imagesRef = useRef(blok.services.map(() => createRef<HTMLDivElement>()));
  const lottieRef = useRef<LottieViewRef>(null);

  const currentSlideIndex = useRef<number | undefined>(undefined);
  const servicesRef = useRef(blok.services.map(() => createRef<ServiceRef>()));

  const slidesTimeline = useRef<gsap.core.Timeline>(undefined);
  slidesTimeline.current = useSetupTimeline(setupSlidesTimeline, {
    servicesRef,
  });

  const imagesTimeline = useRef<gsap.core.Timeline>(undefined);
  imagesTimeline.current = useSetupTimeline(setupImagesTimeline, {
    elementRef,
    imageContainers,
  });

  const lottieTimeline = useRef<gsap.core.Timeline>(undefined);
  lottieTimeline.current = useSetupTimeline(setupLottieTimeline, {
    lottieRef,
  });

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
      vars: {
        start: 'top center',
        // end: 'bottom top-=350%',
        onEnter: () => {
          imagesTimeline?.current?.tweenTo(`slide_0`);
          lottieTimeline?.current?.tweenTo(`slide_0`);
        },
        // onLeave: () => {
        //   gsap.to(elementRef.current, { opacity: 0, duration: 0.25 });
        // },
        // onEnterBack: () => {
        //   gsap.to(elementRef.current, { opacity: 1, duration: 0.25 });
        // },
      },
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imagesContainerRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: elementRef,
    vars: {
      start: 'top top',
      end: `bottom top-=${blok.services.length * 250}%`,
      snap: {
        snapTo: 1 / (blok.services.length - 1),
        directional: false,
      },
      onUpdate: ({ progress }) => {
        const nbItems = blok.services.length;
        const nbLottieItems = 7;
        const index = Math.floor(nbItems * progress);

        const lottieProgress = (progress * (nbItems - 1)) / (nbLottieItems - 1);

        if (lottieRef?.current) {
          lottieRef?.current?.setProgress(lottieProgress % 1);
        }

        if (typeof index === 'number' && currentSlideIndex.current !== index && progress < 1) {
          slidesTimeline?.current?.tweenTo(`slide_${index}`);
          imagesTimeline?.current?.tweenTo(`slide_${index}`);
          // lottieTimeline?.current?.tweenTo(`slide_${index}`);
        }

        currentSlideIndex.current = index;
      },
    },
  });

  const [imageContainerHeight, setImageContainerHeight] = useState(0);

  useEffect(() => {
    if (!imageContainers?.current[0].current?.clientHeight) return;
    setImageContainerHeight(imageContainers?.current[0].current?.clientHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageContainers?.current[0]?.current?.clientHeight]);

  useEffect(() => {
    ScrollTrigger.refresh();
    if (ScrollTrigger.isTouch) {
      ScrollTrigger.normalizeScroll(true);
    }
  }, [imageContainerHeight]);

  return (
    <div
      data-block="services"
      ref={elementRef}
      className={classNames(styles.services, className)}
      {...sbEditable(blok)}
      // style={{ marginBottom: `calc((${imageContainerHeight}px - 100vh)/2)` }}
    >
      <div className={styles.imagesContainer} ref={imagesContainerRef}>
        {blok.services.map(({ image, _uid }, index) => (
          <div className={styles.imageContainer} ref={imageContainers.current[index]} key={_uid}>
            <Image
              src={image.filename}
              srcDesktop={image.filename}
              layout="fill"
              alt={image.alt}
              className={classNames(styles.image, 'abs-fill')}
              sizes={`${cleanMediaQueries.MIN_768} 512px, ${cleanMediaQueries.MIN_1920} 512px, 100vw`}
              ref={imagesRef.current[index]}
            />
          </div>
        ))}
        <LottieView
          ref={lottieRef}
          id="services-full-looping"
          loop={false}
          className={classNames(styles.symbol, 'abs-center')}
        >
          <div className={styles.blackBackground} />
        </LottieView>
      </div>

      <div className={styles.servicesContainer} ref={servicesContainerRef}>
        {blok.services.map((service, index) => (
          <Service
            ref={servicesRef.current[index]}
            blok={service}
            key={service._uid}
            index={index + 1}
            className={styles.service}
          />
        ))}
      </div>
    </div>
  );
}
